/** Sandbox workspace-mount operational issues. */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY } from "../issue-enums";

export const SANDBOX_ISSUES = {
  /**
   * A sandbox start attached fewer workspaces than it resolved, or could not
   * state its mount topology to the in-sandbox enforcers.
   *
   * ## Why this code exists
   *
   * `ExternalSandboxMountService` degrades on purpose: a mount that fails
   * leaves a missing folder rather than a dead sandbox. That is the right
   * trade and it was implemented as `logger.error` and nothing else — so a
   * fleet-wide fault (a revoked IAM role, a missing bucket var, an exec that
   * stopped running as root) refused every workspace for every organization
   * with no alert, nothing red, and no ticket.
   *
   * ## Deliberately reported WITHOUT an entity
   *
   * `report()` is called with neither `entityType` nor `entityId`. The adapter
   * normalizes both to `""` and the columns are `String @default("")`, so the
   * compound unique `(organizationId, code, entityType, entityId)` collapses to
   * ONE ROW PER ORGANIZATION and `count` carries the recurrence. Reporting per
   * SANDBOX would be ~1000 rows for a single fleet-wide cause, and a channel
   * that floods on its worst day is muted before its second one.
   *
   * `NULLS DISTINCT` never applies here precisely because those columns are NOT
   * NULL with an empty-string default — a nullable pair would defeat the upsert
   * and create a row per call, which is the opposite of the intent.
   *
   * ## Admin-only, on purpose
   *
   * No `fault`/`userTitle`, so it stays off every customer surface. The causes
   * are ours — IAM, buckets, the sandbox image — and none of them is an action
   * a customer can take.
   */
  SANDBOX_WORKSPACE_MOUNT_DEGRADED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "A sandbox attached fewer workspaces than were resolved for it, or could not state its mount topology inside the sandbox",
    severity: "high",
    // Re-observed on every sandbox start in the organization, so a cause that is still there
    // reports again within the hour on any org that uses sandboxes at all. Three days spans a
    // weekend of no starts without resolving a live fault.
    resolvesAfterQuietHours: 72,
    remedy:
      "Open the Issues row and read `rawPayload`: it names the sandbox, how many mounts were requested against how many attached, and the failure reason verbatim. " +
      "If `outcome` is `aborted`, nothing was attempted and the cause is shared rather than per-workspace — check WORKSPACES_SANDBOX_ROLE_ARN, WORKSPACES_AWS_BUCKET_NAME/REGION and the STS credentials, and check the role's MaxSessionDuration, which defaults to 3600s and silently fails every AssumeRole when the requested duration exceeds it. " +
      "If `outcome` is `attempted` with a non-empty `failed`, the cause is per-workspace: read each reason. `NEXUS_MOUNT_REFUSED_UNSAFE_ROOT` means the sandbox's /mnt is not root-owned; `NEXUS_MOUNT_REFUSED_ROOT_UNREADABLE` means the image has no stat or no /mnt; exit 127 means mount-s3 is missing from the image. " +
      "If `channelWritten` is false, workspaces may be attached but the in-sandbox enforcers were not told, so they refuse every write under /mnt for that sandbox until it is recycled. " +
      "A count climbing across many organizations at once is a fleet fault, not a tenant fault — suspect the sandbox image or the IAM role before any single workspace."
  }
} as const satisfies Record<string, IssueDefinition>;
